// Right-side detail drawer for a single call: metadata, latency stats, the
// recording (signed URL fetched lazily on play), transcript and analysis.
// Voice-pipeline calls always get a Recording section: when there is no audio
// it says why (live call, typed chat, no audio, upload pending, or a real
// loss: an orphaned call or a failed upload).

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  getCall,
  getRecordingUrl,
  type CallRecord,
  type LatencyBucket,
  type TranscriptEntry,
} from "../../services/analytics";
import { getCallEvaluation, runCallEvaluation, type CallEvaluation } from "../../services/evals";
import { scoreTone } from "../evals/RubricSummary";
import { AnalysisBadge, ModeChip, SentimentCell, SourceChip } from "./bits";
import {
  endedWithin,
  formatDateTime,
  formatDurationMs,
  inputLabel,
  RECORDING_ATTACH_GRACE_MS,
} from "./format";
import { PHOENIX_URL } from "../../config/env";
import { InterruptionChip } from "../InterruptionChip";

function LatencyRow({ label, bucket }: { label: string; bucket: LatencyBucket | null | undefined }) {
  return (
    <tr>
      <td>{label}</td>
      <td>{bucket ? `${Math.round(bucket.p50)} ms` : "—"}</td>
      <td>{bucket ? `${Math.round(bucket.p95)} ms` : "—"}</td>
      <td>{bucket ? `${Math.round(bucket.max)} ms` : "—"}</td>
      <td>{bucket ? bucket.count : "—"}</td>
    </tr>
  );
}

function ChipList({ label, items, tone }: { label: string; items: string[]; tone?: string }) {
  if (items.length === 0) return null;
  return (
    <div className="chip-group">
      <span className="chip-group-label">{label}</span>
      <span className="chip-row">
        {items.map((it) => (
          <span key={it} className={`chip${tone ? ` chip-${tone}` : ""}`}>{it}</span>
        ))}
      </span>
    </div>
  );
}

// Whether the row's audio counters show any mic or agent audio: true/false,
// or null when the row has no counters (older rows).
function hadAudio(call: CallRecord): boolean | null {
  const audio = call.latencyStats?.audio;
  if (!audio) return null;
  return (audio.mic_bytes ?? 0) > 0 || (audio.agent_audio_bytes ?? 0) > 0;
}

// Why a voice-pipeline call has no recording. Only an orphaned call, or a
// closed call that had audio and is past the upload grace window, is a loss.
// Zero-turn calls usually DO have audio (the greeting plays, the mic streams),
// so they are judged by the audio counters, not by the turn count.
function MissingRecording({ call }: { call: CallRecord }) {
  if (!call.endedAt) {
    return (
      <div className="recording-note neutral">
        <strong>Call in progress</strong>
        The recording is saved when the call ends.
      </div>
    );
  }
  if (call.endReason === "orphaned") {
    return (
      <div className="recording-note problem">
        <strong>Recording missing</strong>
        The call never closed normally, so no recording was linked to it. Search the backend logs
        for call id <span className="mono">{call.id}</span>.
      </div>
    );
  }
  // Counters first: a call can start in voice (greeting spoken, mic open)
  // and switch to typing, which stores input_mode "text" yet records audio.
  const audio = hadAudio(call);
  if (call.inputMode === "text" && audio !== true) {
    return (
      <div className="recording-note info">
        <strong>No recording: this chat was typed</strong>
        There was no audio to record. The transcript below is the whole conversation.
      </div>
    );
  }
  const expectsAudio =
    call.inputMode === "voice" || call.inputMode === "mixed" || audio === true;
  if (expectsAudio) {
    if (endedWithin(call.endedAt, RECORDING_ATTACH_GRACE_MS)) {
      return (
        <div className="recording-note neutral">
          <strong>Recording not attached yet</strong>
          The audio uploads after the call ends, which can take up to about a minute and a
          half. Reopen the call shortly.
        </div>
      );
    }
    return (
      <div className="recording-note problem">
        <strong>Recording missing</strong>
        The upload failed or timed out. Search the backend logs for call id{" "}
        <span className="mono">{call.id}</span>.
      </div>
    );
  }
  if (call.userMsgCount === 0) {
    return (
      <div className="recording-note neutral">
        <strong>No recording</strong>
        {audio === false
          ? "No audio on this connection: no mic audio arrived and the agent never spoke."
          : "No user turn on this connection, and this older row has no audio counters to show whether a recording was expected."}
      </div>
    );
  }
  return (
    <div className="recording-note neutral">
      <strong>No recording</strong>
      This call is older than input-mode tracking, so there is no way to tell whether it had
      audio.
    </div>
  );
}

function Meta({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="meta-item">
      <span className="meta-label">{label}</span>
      <span className="meta-value">{children}</span>
    </div>
  );
}

export function CallDrawer({ callId, onClose }: { callId: string; onClose: () => void }) {
  const [call, setCall] = useState<CallRecord | null>(null);
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [recordingUrl, setRecordingUrl] = useState<string | null>(null);
  const [recordingError, setRecordingError] = useState<string | null>(null);
  const [loadingRecording, setLoadingRecording] = useState(false);

  // Rubric evaluation: fetched lazily, fail-soft (most calls are unsampled).
  const [evaluation, setEvaluation] = useState<CallEvaluation | null>(null);
  const [judging, setJudging] = useState(false);
  const [judgeError, setJudgeError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setCall(null);
    setTranscript([]);
    setRecordingUrl(null);
    setRecordingError(null);
    setEvaluation(null);
    setJudgeError(null);
    getCall(callId)
      .then((r) => {
        if (cancelled) return;
        setCall(r.call);
        setTranscript(r.transcript || []);
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : "Failed to load call"))
      .finally(() => !cancelled && setLoading(false));
    getCallEvaluation(callId)
      .then((ev) => !cancelled && setEvaluation(ev))
      .catch(() => {}); // 404 = never evaluated; the section offers "Judge now"
    return () => {
      cancelled = true;
    };
  }, [callId]);

  async function judgeNow() {
    setJudging(true);
    setJudgeError(null);
    try {
      await runCallEvaluation(callId);
      setEvaluation(await getCallEvaluation(callId));
    } catch (e) {
      setJudgeError(e instanceof Error ? e.message : "Evaluation failed");
    } finally {
      setJudging(false);
    }
  }

  // Close on Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Only fetch the signed URL when the user actually wants to listen.
  async function loadRecording() {
    setLoadingRecording(true);
    setRecordingError(null);
    try {
      const r = await getRecordingUrl(callId);
      setRecordingUrl(r.url);
    } catch (e) {
      setRecordingError(e instanceof Error ? e.message : "Failed to fetch recording");
    } finally {
      setLoadingRecording(false);
    }
  }

  const latency = call?.latencyStats;
  const input = call ? inputLabel(call) : null;

  return (
    <>
      <div className="drawer-overlay" onClick={onClose} />
      <aside className="drawer">
        <div className="drawer-head">
          <div>
            <div className="drawer-title">
              {call ? (
                <>
                  <Link to={`/guides/${encodeURIComponent(call.guideId)}?tab=insights`}>
                    {call.guideName || call.guideId}
                  </Link>{" "}
                  <ModeChip call={call} />
                  <SourceChip source={call.source} />
                </>
              ) : (
                "Call detail"
              )}
            </div>
            {call && (
              <div className="drawer-sub muted">
                {formatDateTime(call.startedAt)} · {formatDurationMs(call.durationMs)}{" "}
                <AnalysisBadge status={call.analysisStatus} />
              </div>
            )}
          </div>
          <div className="spacer" />
          <button className="btn btn-ghost" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="drawer-body">
          {loading && <div className="state-box">Loading call…</div>}
          {error && <div className="state-box error">{error}</div>}

          {call && (
            <>
              {call.callSummary && <p className="call-summary">{call.callSummary}</p>}

              <div className="drawer-section">
                <h3>Details</h3>
                <div className="meta-grid">
                  <Meta label="User"><span className="mono" title={call.uid}>{call.uid}</span></Meta>
                  <Meta label="Session"><span className="mono" title={call.sessionId}>{call.sessionId}</span></Meta>
                  <Meta label="Companion">{call.guideId} · v{call.guideVersion}</Meta>
                  <Meta label="Started">{formatDateTime(call.startedAt)}</Meta>
                  <Meta label="Ended">{formatDateTime(call.endedAt)}</Meta>
                  <Meta label="Duration">{formatDurationMs(call.durationMs)}</Meta>
                  <Meta label="Input">
                    <span title={input?.title}>
                      {input?.detail} · {call.mode} pipeline
                    </span>
                  </Meta>
                  <Meta label="Messages">{call.userMsgCount} user / {call.modelMsgCount} model</Meta>
                  <Meta label="End reason">{call.endReason || "—"}</Meta>
                  <Meta label="Tools">
                    {call.toolCallCount > 0
                      ? `${call.toolCallCount} calls · ${formatDurationMs(call.toolMsTotal)}`
                      : "none"}
                  </Meta>
                  <Meta label="Sentiment">
                    <SentimentCell score={call.sentimentScore} />
                    {call.sentimentLabel ? ` (${call.sentimentLabel})` : ""}
                  </Meta>
                  {PHOENIX_URL && (call.traceId || call.conversationId) && (
                    <Meta label="Traces">
                      {call.traceId && (
                        <a
                          href={`${PHOENIX_URL}/redirects/traces/${encodeURIComponent(call.traceId)}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Call
                        </a>
                      )}
                      {call.traceId && call.conversationId && " · "}
                      {call.conversationId && (
                        <a
                          href={`${PHOENIX_URL}/redirects/sessions/${encodeURIComponent(call.conversationId)}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Conversation
                        </a>
                      )}
                    </Meta>
                  )}
                </div>
              </div>

              {latency && (latency.first_audio_ms || latency.turn_ms) && (
                <div className="drawer-section">
                  <h3>Latency</h3>
                  <table className="latency-table">
                    <thead>
                      <tr><th></th><th>p50</th><th>p95</th><th>max</th><th>n</th></tr>
                    </thead>
                    <tbody>
                      <LatencyRow label="First audio" bucket={latency.first_audio_ms} />
                      <LatencyRow label="Turn" bucket={latency.turn_ms} />
                    </tbody>
                  </table>
                </div>
              )}

              {call.hasRecording ? (
                <div className="drawer-section">
                  <h3>Recording {call.recordingDurationS != null && (
                    <span className="muted">({formatDurationMs(call.recordingDurationS * 1000)})</span>
                  )}</h3>
                  {recordingUrl ? (
                    <audio className="call-audio" controls autoPlay src={recordingUrl} />
                  ) : (
                    <button className="btn" onClick={loadRecording} disabled={loadingRecording}>
                      {loadingRecording ? "Fetching recording…" : "▶ Play recording"}
                    </button>
                  )}
                  {recordingError && <div className="error-text">{recordingError}</div>}
                  {(call.inputMode === "mixed" || call.inputMode === "text") && (
                    <div className="recording-note subtle">
                      Typed turns and their replies are not in the audio.
                    </div>
                  )}
                </div>
              ) : (
                call.mode === "voice" && (
                  <div className="drawer-section">
                    <h3>Recording</h3>
                    <MissingRecording call={call} />
                  </div>
                )
              )}

              <div className="drawer-section">
                <h3>
                  Rubric scores{" "}
                  {evaluation?.status === "complete" && evaluation.overallScore !== null && (
                    <span className={`rubric-score sm ${scoreTone(evaluation.overallScore)}`}>
                      {Math.round(evaluation.overallScore * 100)}%
                    </span>
                  )}
                </h3>
                {evaluation?.status === "complete" ? (
                  <div className="rubric-detail">
                    {evaluation.criteria.map((c) => (
                      <details key={c.key} className="rubric-criterion">
                        <summary>
                          {c.applicable === false ? (
                            <span className="rubric-score sm muted">N/A</span>
                          ) : (
                            <span className={`rubric-score sm ${scoreTone(c.score)}`}>
                              {Math.round(c.score * 100)}%
                            </span>
                          )}{" "}
                          {c.key.replaceAll("_", " ")}
                        </summary>
                        <div className="rubric-rationale">{c.rationale}</div>
                      </details>
                    ))}
                    <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>
                      judged by {evaluation.judgeModel} · {evaluation.rubricId}
                      {" · "}
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={judgeNow}
                        disabled={judging}
                      >
                        {judging ? "Judging…" : "Re-judge"}
                      </button>
                    </div>
                    {judgeError && <div className="error-text">{judgeError}</div>}
                  </div>
                ) : (
                  <div>
                    <button className="btn btn-sm" onClick={judgeNow} disabled={judging}>
                      {judging ? "Judging…" : "Judge this call"}
                    </button>
                    {evaluation?.status === "failed" && (
                      <span className="muted" style={{ marginLeft: 8, fontSize: 12 }}>
                        last attempt failed
                      </span>
                    )}
                    {judgeError && <div className="error-text">{judgeError}</div>}
                  </div>
                )}
              </div>

              <ChipList label="Emotions" items={call.emotions} />
              <ChipList label="Topics" items={call.topics} />
              <ChipList label="Flags" items={call.flags} tone="flag" />

              <div className="drawer-section">
                <h3>Transcript</h3>
                {transcript.length === 0 ? (
                  <div className="state-box">No transcript available.</div>
                ) : (
                  <div className="transcript">
                    {transcript.map((t, i) => (
                      <div key={i} className={`msg ${t.role === "user" ? "user" : "guide"}`}>
                        {t.text}
                        {t.interruption && (
                          <InterruptionChip
                            info={{
                              heardRatio: t.interruption.heardRatio ?? 0,
                              intendedText: t.interruption.intendedText ?? "",
                              spokenText: t.text,
                              interruptedAtMs: t.interruption.interruptedAtMs,
                            }}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </aside>
    </>
  );
}
